/**
 * Webhook Endpoints (Sprint C14 Lane B WP-8 + Lane C WP-5)
 *
 * FI-tenant-scoped CRUD + HMAC secret rotation for outbound webhook
 * endpoint registrations. An FI may register multiple endpoints (e.g.,
 * a primary production URL plus a staging URL) and rotate the secret
 * on each independently.
 *
 * Backend: platform-admin-api routes mounted at
 *   GET    /api/admin/tenants/me/webhook-endpoints
 *   POST   /api/admin/tenants/me/webhook-endpoints
 *   GET    /api/admin/tenants/me/webhook-endpoints/{endpointId}
 *   PUT    /api/admin/tenants/me/webhook-endpoints/{endpointId}
 *   DELETE /api/admin/tenants/me/webhook-endpoints/{endpointId}
 *   POST   /api/admin/tenants/me/webhook-endpoints/{endpointId}/rotate-secret
 *
 * Types: TenantWebhookConfig.components.schemas.* from
 * `@aaimplatform/institutional-types`. No inline interfaces.
 *
 * Event types (fi-30): the register and edit dialogs pick eventTypes from a
 * multi-select over WebhookEventType (utils/webhook-event-types.ts), one
 * labeled kit Checkbox per event in a fieldset, where a comma-separated text
 * field admitted any string. The table lists an endpoint's event types by
 * label.
 *
 * SECURITY: the plaintext HMAC secret is returned in the response body
 * ONCE on POST and ONCE on rotate-secret. The page surfaces it in a
 * one-time disclosure modal -- never persisted to local state outside
 * the modal lifetime, never logged, never sent to any other endpoint.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  EmptyState,
  Input,
  Skeleton,
  Switch,
  formatDateTime,
} from '@aaimplatform/ui';
import type { DataTableColumn } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/tenant-webhook-config';
import { tenantWebhookConfigClient } from '../../api/client';
import { WEBHOOK_ENDPOINTS_QUERY_KEY, useWebhookEndpoints } from '../../hooks/useWebhookEndpoints';
import {
  WEBHOOK_EVENT_TYPES,
  WEBHOOK_EVENT_TYPE_LABELS,
  webhookEventTypeLabel,
  type WebhookEventType,
} from '../../utils/webhook-event-types';

type TenantWebhookEndpoint = components['schemas']['TenantWebhookEndpoint'];
type TenantWebhookEndpointCreate = components['schemas']['TenantWebhookEndpointCreate'];
type TenantWebhookEndpointUpdate = components['schemas']['TenantWebhookEndpointUpdate'];

const NAME_MAX = 200;
const URL_MAX = 2048;
const HTTPS_URL = /^https:\/\/[^\s]{1,2048}$/i;
const HTTP_OR_HTTPS_URL = /^https?:\/\/[^\s]{1,2048}$/i;
const URL_SCHEME_MESSAGE = 'URL must be an absolute HTTPS URL';

/**
 * The canonical spec admits an absolute HTTPS URL and rejects http:// outside
 * dev (tenant-webhook-config.openapi.yaml TenantWebhookEndpointCreate.url). The
 * page admitted http:// on prod, for HMAC-signed pledge payloads, and its
 * helper text advertised it (fi-g4-6). A Vite dev build still admits http:// so
 * a local receiver can be registered.
 */
export function isAdmissibleWebhookUrl(url: string, allowHttp: boolean = import.meta.env.DEV): boolean {
  return (allowHttp ? HTTP_OR_HTTPS_URL : HTTPS_URL).test(url);
}

/**
 * What the event-type choice reads while no box is checked, which is the
 * subscription an empty eventTypes array means (tenant-webhook-config
 * TenantWebhookEndpoint.eventTypes). It was an example list for a text field;
 * the dialog now offers only the events the platform emits (fi-30), and an
 * earlier example suggested pledge.acknowledged and collateral.value_drop,
 * which nothing emits (fi-g4-8).
 */
export const EVENT_TYPES_PLACEHOLDER = 'All event types';

/** The chosen event types after one box is checked or cleared, in spec order. */
function toggledEventTypes(
  current: readonly WebhookEventType[],
  value: WebhookEventType,
  checked: boolean,
): WebhookEventType[] {
  const chosen = new Set(current);
  if (checked) chosen.add(value);
  else chosen.delete(value);
  return WEBHOOK_EVENT_TYPES.filter((type) => chosen.has(type));
}

/** Whether two event-type lists name the same events, in any order. */
function sameEventTypes(a: readonly WebhookEventType[], b: readonly WebhookEventType[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((value) => right.has(value));
}

type ModalMode = 'create' | 'edit' | null;

interface FormState {
  name: string;
  url: string;
  eventTypes: WebhookEventType[];
  enabled: boolean;
}

interface SecretDisclosure {
  endpointId: string;
  endpointName: string;
  hmacSecret: string;
  rotatedAt?: string;
  context: 'created' | 'rotated';
}

const BLANK_FORM: FormState = {
  name: '',
  url: '',
  eventTypes: [],
  enabled: true,
};

function formFromEndpoint(endpoint: TenantWebhookEndpoint): FormState {
  return {
    name: endpoint.name,
    url: endpoint.url,
    eventTypes: [...endpoint.eventTypes],
    enabled: endpoint.enabled,
  };
}

interface ValidationOk<T> {
  ok: true;
  value: T;
}
interface ValidationErr {
  ok: false;
  message: string;
}

function validateCreate(form: FormState): ValidationOk<TenantWebhookEndpointCreate> | ValidationErr {
  if (form.name.length === 0 || form.name.length > NAME_MAX) {
    return { ok: false, message: `Name is required and must be at most ${NAME_MAX} characters` };
  }
  if (form.url.length === 0 || form.url.length > URL_MAX) {
    return { ok: false, message: `URL is required and must be at most ${URL_MAX} characters` };
  }
  if (!isAdmissibleWebhookUrl(form.url)) {
    return { ok: false, message: URL_SCHEME_MESSAGE };
  }
  return {
    ok: true,
    value: {
      name: form.name,
      url: form.url,
      eventTypes: form.eventTypes,
      enabled: form.enabled,
    },
  };
}

function validateUpdate(
  form: FormState,
  baseline: TenantWebhookEndpoint,
): ValidationOk<TenantWebhookEndpointUpdate> | ValidationErr {
  if (form.name.length === 0 || form.name.length > NAME_MAX) {
    return { ok: false, message: `Name is required and must be at most ${NAME_MAX} characters` };
  }
  if (form.url.length === 0 || form.url.length > URL_MAX) {
    return { ok: false, message: `URL is required and must be at most ${URL_MAX} characters` };
  }
  if (!isAdmissibleWebhookUrl(form.url)) {
    return { ok: false, message: URL_SCHEME_MESSAGE };
  }
  const update: TenantWebhookEndpointUpdate = {};
  if (form.name !== baseline.name) update.name = form.name;
  if (form.url !== baseline.url) update.url = form.url;
  // The boxes carry no order, so the choice is compared as a set: checking and
  // clearing a box sends nothing, even where the stored order differs.
  if (!sameEventTypes(form.eventTypes, baseline.eventTypes)) update.eventTypes = form.eventTypes;
  if (form.enabled !== baseline.enabled) update.enabled = form.enabled;
  return { ok: true, value: update };
}

// ---------------------------------------------------------------------------
// SecretDisclosureModal -- one-time presentation of a freshly-minted secret
// ---------------------------------------------------------------------------

function SecretDisclosureModal({
  disclosure,
  onClose,
}: {
  disclosure: SecretDisclosure | null;
  onClose: () => void;
}) {
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!disclosure) return;
    navigator.clipboard.writeText(disclosure.hmacSecret).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  // Reset the local view-state when the disclosure changes or closes.
  const handleClose = () => {
    setShowSecret(false);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog
      open={disclosure !== null}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={
        disclosure?.context === 'rotated'
          ? 'New HMAC secret (one-time disclosure)'
          : 'HMAC secret (one-time disclosure)'
      }
      size="lg"
      isDismissable={false}
      isKeyboardDismissDisabled={false}
      footer={
        <Button variant="primary" onClick={handleClose}>
          I have captured the secret
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
          <div className="text-sm font-sans text-error leading-normal">
            <strong>Capture this secret now.</strong> It will not be shown again
            and cannot be retrieved later. If you lose it, you must rotate the
            secret to generate a new value.
          </div>
        </div>

        {disclosure && (
          <>
            <div>
              <div className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                Endpoint
              </div>
              <div className="text-sm font-sans text-primary leading-normal">
                {disclosure.endpointName}
              </div>
            </div>

            <div>
              <div className="text-xs font-sans font-semibold uppercase text-tertiary mb-2">
                HMAC secret
              </div>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={disclosure.hmacSecret}
                    readOnly
                    className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-mono bg-elevated"
                  />
                  <button
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-recessed rounded-sm"
                    aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                  >
                    {showSecret ? (
                      <EyeOff className="w-4 h-4 text-tertiary" />
                    ) : (
                      <Eye className="w-4 h-4 text-tertiary" />
                    )}
                  </button>
                </div>
                <Button variant="ghost" size="sm" onClick={handleCopy} aria-label="Copy secret">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              {/* The kit formatter prints en-US with the zone named; a bare
                  toLocaleString() printed the viewer's locale with no zone. */}
              {disclosure.rotatedAt && (
                <div className="text-xs font-sans text-tertiary leading-normal mt-2">
                  Rotated at {formatDateTime(disclosure.rotatedAt)}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// EndpointFormModal -- shared shell for create + edit
// ---------------------------------------------------------------------------

function EndpointFormModal({
  mode,
  baseline,
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
}: {
  mode: ModalMode;
  baseline?: TenantWebhookEndpoint;
  onClose: () => void;
  onSubmit: (form: FormState) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
}) {
  const [form, setForm] = useState<FormState>(
    baseline ? formFromEndpoint(baseline) : BLANK_FORM,
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form when baseline changes (e.g. edit a different row).
  // We use a key-on-baseline pattern via parent; this is a defensive guard.
  useMemo(() => {
    setForm(baseline ? formFromEndpoint(baseline) : BLANK_FORM);
    setValidationError(null);
  }, [baseline?.id]);

  const handleSubmit = () => {
    setValidationError(null);
    if (mode === 'create') {
      const result = validateCreate(form);
      if (!result.ok) {
        setValidationError(result.message);
        return;
      }
    } else if (mode === 'edit' && baseline) {
      const result = validateUpdate(form, baseline);
      if (!result.ok) {
        setValidationError(result.message);
        return;
      }
    }
    onSubmit(form);
  };

  return (
    <Dialog
      open={mode !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={mode === 'create' ? 'Register webhook endpoint' : 'Edit webhook endpoint'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {mode === 'create' ? 'Create endpoint' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {(validationError || errorMessage) && (
          <div className="bg-error/5 border border-error/20 rounded-sm p-3 flex items-center gap-3">
            <AlertCircle className="w-4 h-4 text-error flex-shrink-0" />
            <div className="text-sm font-sans text-error leading-normal">
              {validationError ?? errorMessage}
            </div>
          </div>
        )}

        {/* Kit Input and Switch carry the label association (htmlFor / id and
            role="switch" with aria-checked) that the hand-rolled controls
            lacked (UI run 2026-09-27, fi-g4-14). */}
        <Input
          id="webhook-endpoint-name"
          label="Name"
          type="text"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Primary production receiver"
          maxLength={NAME_MAX}
        />

        <Input
          id="webhook-endpoint-url"
          label="URL"
          type="url"
          value={form.url}
          onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
          placeholder="https://api.example.com/webhooks/aaim"
          maxLength={URL_MAX}
          helperText="Absolute HTTPS URL. Aaim signs every payload with this endpoint's HMAC secret using HMAC-SHA256 in the X-Aaim-Signature header."
          className="font-mono"
        />

        {/* A multi-select over the events the platform emits (fi-30): one kit
            Checkbox per WebhookEventType, labeled, with the event name the
            receiver sees in X-Aaim-Event beneath each label. The text field
            it replaces sent any string the tenant typed. */}
        <fieldset>
          <legend className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-2">
            Event types
          </legend>
          <p className="text-xs font-sans text-tertiary leading-normal mb-3">
            Leave every box clear to subscribe to every event type.{' '}
            <span className="text-secondary">
              {form.eventTypes.length === 0
                ? `${EVENT_TYPES_PLACEHOLDER}.`
                : `${form.eventTypes.length} of ${WEBHOOK_EVENT_TYPES.length} chosen.`}
            </span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {WEBHOOK_EVENT_TYPES.map((value) => (
              <Checkbox
                key={value}
                id={`webhook-event-type-${value.replace(/[._]/g, '-')}`}
                label={WEBHOOK_EVENT_TYPE_LABELS[value]}
                helperText={value}
                checked={form.eventTypes.includes(value)}
                onChange={(checked) =>
                  setForm((prev) => ({ ...prev, eventTypes: toggledEventTypes(prev.eventTypes, value, checked) }))
                }
              />
            ))}
          </div>
        </fieldset>

        <div className="py-2 border-t border-recessed pt-4">
          <Switch
            label="Enabled"
            description="When disabled, deliveries are suppressed without removing this registration."
            isSelected={form.enabled}
            onChange={(isSelected) => setForm((prev) => ({ ...prev, enabled: isSelected }))}
          />
        </div>

        {mode === 'create' && (
          <div className="bg-elevated border border-recessed rounded-sm p-3">
            <div className="text-xs font-sans text-tertiary leading-normal">
              Aaim generates a 256-bit HMAC secret when the endpoint is created
              and shows it once, in the next dialog. Capture it before you close
              that dialog.
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// DeleteConfirmationModal
// ---------------------------------------------------------------------------

function DeleteConfirmationModal({
  endpoint,
  onClose,
  onConfirm,
  isDeleting,
  errorMessage,
}: {
  endpoint: TenantWebhookEndpoint | null;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  errorMessage: string | null;
}) {
  return (
    <Dialog
      open={endpoint !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Delete webhook endpoint"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Delete endpoint
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {errorMessage && <Alert type="error">{errorMessage}</Alert>}
        <div className="text-sm font-sans text-primary leading-normal">
          Permanently delete the webhook endpoint{' '}
          <strong>{endpoint?.name}</strong>?
        </div>
        <div className="text-sm font-sans text-tertiary leading-normal">
          Subsequent webhook deliveries that would have routed to this URL will
          be dropped. The HMAC secret is destroyed with the row. This cannot be
          undone.
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// RotateConfirmationModal
// ---------------------------------------------------------------------------

/**
 * Rotation replaces the endpoint's secret at once: the handler overwrites it
 * with no overlap window (webhook-endpoints.routes.ts rotate-secret), so the
 * receiver's copy stops verifying the moment the call returns. The row button
 * ran the rotation on one click (fi-g4-7).
 */
function RotateConfirmationModal({
  endpoint,
  onClose,
  onConfirm,
  isRotating,
}: {
  endpoint: TenantWebhookEndpoint | null;
  onClose: () => void;
  onConfirm: () => void;
  isRotating: boolean;
}) {
  return (
    <Dialog
      open={endpoint !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Rotate HMAC secret"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isRotating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={isRotating}>
            {isRotating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Rotate secret
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-sm font-sans text-primary leading-normal">
          Rotate the HMAC secret for <strong>{endpoint?.name}</strong>?
        </div>
        <div className="text-sm font-sans text-tertiary leading-normal">
          The current secret stops verifying immediately. Every delivery after
          the rotation is signed with the new secret, so your receiver rejects
          them until it has the new one. The new secret is shown once, in the
          next dialog.
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function WebhookEndpointsPage() {
  const queryClient = useQueryClient();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingEndpoint, setEditingEndpoint] = useState<TenantWebhookEndpoint | null>(null);
  const [deletingEndpoint, setDeletingEndpoint] = useState<TenantWebhookEndpoint | null>(null);
  const [rotatingEndpoint, setRotatingEndpoint] = useState<TenantWebhookEndpoint | null>(null);
  const [secretDisclosure, setSecretDisclosure] = useState<SecretDisclosure | null>(null);
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  // Rotate and delete failures used to say nothing: neither mutation had an
  // error path, so the delete dialog stayed open and silent (fi-g4-7).
  const [actionErrorMessage, setActionErrorMessage] = useState<string | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);

  const listQuery = useWebhookEndpoints();

  const createMutation = useMutation({
    mutationFn: async (form: FormState) => {
      const validation = validateCreate(form);
      if (!validation.ok) throw new Error(validation.message);
      const { data, error } = await tenantWebhookConfigClient.POST('/tenants/me/webhook-endpoints', {
        body: validation.value,
      });
      if (error || !data) throw new Error('Failed to create webhook endpoint');
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: WEBHOOK_ENDPOINTS_QUERY_KEY });
      setModalMode(null);
      setFormErrorMessage(null);
      setSecretDisclosure({
        endpointId: data.id,
        endpointName: data.name,
        hmacSecret: data.hmacSecret,
        rotatedAt: data.createdAt,
        context: 'created',
      });
    },
    onError: (error: Error) => {
      setFormErrorMessage(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ form, baseline }: { form: FormState; baseline: TenantWebhookEndpoint }) => {
      const validation = validateUpdate(form, baseline);
      if (!validation.ok) throw new Error(validation.message);
      const { data, error } = await tenantWebhookConfigClient.PUT(
        '/tenants/me/webhook-endpoints/{endpointId}',
        {
          params: { path: { endpointId: baseline.id } },
          body: validation.value,
        },
      );
      if (error || !data) throw new Error('Failed to update webhook endpoint');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WEBHOOK_ENDPOINTS_QUERY_KEY });
      setModalMode(null);
      setEditingEndpoint(null);
      setFormErrorMessage(null);
    },
    onError: (error: Error) => {
      setFormErrorMessage(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (endpoint: TenantWebhookEndpoint) => {
      const { error } = await tenantWebhookConfigClient.DELETE(
        '/tenants/me/webhook-endpoints/{endpointId}',
        { params: { path: { endpointId: endpoint.id } } },
      );
      if (error) throw new Error('Failed to delete webhook endpoint');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WEBHOOK_ENDPOINTS_QUERY_KEY });
      setDeletingEndpoint(null);
      setDeleteErrorMessage(null);
      setActionErrorMessage(null);
    },
    onError: (_error: Error, endpoint) => {
      const message = `Deleting ${endpoint.name} failed. Reload the list to see whether it is still registered, then try again.`;
      setDeleteErrorMessage(message);
      setActionErrorMessage(message);
    },
  });

  const rotateMutation = useMutation({
    mutationFn: async (endpoint: TenantWebhookEndpoint) => {
      const { data, error } = await tenantWebhookConfigClient.POST(
        '/tenants/me/webhook-endpoints/{endpointId}/rotate-secret',
        { params: { path: { endpointId: endpoint.id } } },
      );
      if (error || !data) throw new Error('Failed to rotate webhook secret');
      return { data, endpointName: endpoint.name };
    },
    onSuccess: ({ data, endpointName }) => {
      queryClient.invalidateQueries({ queryKey: WEBHOOK_ENDPOINTS_QUERY_KEY });
      setRotatingEndpoint(null);
      setActionErrorMessage(null);
      setSecretDisclosure({
        endpointId: data.endpointId,
        endpointName,
        hmacSecret: data.hmacSecret,
        rotatedAt: data.rotatedAt,
        context: 'rotated',
      });
    },
    onError: (_error: Error, endpoint) => {
      setRotatingEndpoint(null);
      // The handler writes the new secret before it answers, so a failed
      // response does not prove the old secret still verifies; the page says
      // only what it knows.
      setActionErrorMessage(
        `Rotating the HMAC secret for ${endpoint.name} failed, and no new secret was shown. Try again; the next rotation shows its secret once.`,
      );
    },
  });

  const openCreateModal = () => {
    setEditingEndpoint(null);
    setFormErrorMessage(null);
    setModalMode('create');
  };

  const openEditModal = (endpoint: TenantWebhookEndpoint) => {
    setEditingEndpoint(endpoint);
    setFormErrorMessage(null);
    setModalMode('edit');
  };

  const handleFormSubmit = (form: FormState) => {
    if (modalMode === 'create') {
      createMutation.mutate(form);
    } else if (modalMode === 'edit' && editingEndpoint) {
      updateMutation.mutate({ form, baseline: editingEndpoint });
    }
  };

  const isFormSubmitting = createMutation.isPending || updateMutation.isPending;

  const endpointColumns: DataTableColumn<TenantWebhookEndpoint>[] = [
    {
      header: 'Name',
      accessor: 'name',
      cell: (_value, endpoint) => (
        <span className="text-sm font-sans font-medium text-primary leading-normal">{endpoint.name}</span>
      ),
    },
    {
      header: 'URL',
      accessor: 'url',
      cell: (_value, endpoint) => (
        <span
          className="block text-sm font-mono text-secondary leading-normal max-w-md truncate"
          title={endpoint.url}
        >
          {endpoint.url}
        </span>
      ),
    },
    {
      header: 'Events',
      accessor: 'eventTypes',
      cell: (_value, endpoint) =>
        endpoint.eventTypes.length === 0 ? (
          <span className="text-sm font-sans text-tertiary italic">All events</span>
        ) : (
          <span className="text-sm font-sans text-secondary leading-normal">
            {endpoint.eventTypes.map(webhookEventTypeLabel).join(', ')}
          </span>
        ),
    },
    {
      header: 'Status',
      accessor: 'enabled',
      cell: (_value, endpoint) =>
        endpoint.enabled ? (
          <Badge variant="success" size="small">Enabled</Badge>
        ) : (
          <Badge variant="neutral" size="small">Disabled</Badge>
        ),
    },
    {
      header: 'Actions',
      accessor: 'id',
      align: 'right',
      cell: (_value, endpoint) => (
        <div className="flex items-center justify-end gap-1">
          <span title="Rotate HMAC secret">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setActionErrorMessage(null);
                setRotatingEndpoint(endpoint);
              }}
              disabled={rotateMutation.isPending}
              aria-label={`Rotate secret for ${endpoint.name}`}
            >
              {rotateMutation.isPending && rotateMutation.variables?.id === endpoint.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </span>
          <span title="Edit endpoint">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEditModal(endpoint)}
              aria-label={`Edit ${endpoint.name}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
          </span>
          <span title="Delete endpoint">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDeleteErrorMessage(null);
                setDeletingEndpoint(endpoint);
              }}
              aria-label={`Delete ${endpoint.name}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </span>
        </div>
      ),
    },
  ];

  // Only a successful read can say the list is empty. A failed read renders
  // the error alone; before this, `?? []` sent a failure into the empty-state
  // branch too, reporting "no endpoints" nobody had read (fi-g4-15).
  const endpoints = listQuery.isSuccess ? listQuery.data.data : [];

  if (listQuery.isLoading) {
    return (
      <div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <Skeleton className="h-5 w-full max-w-2xl" />
          <Skeleton className="h-10 w-44" />
        </div>
        <Skeleton className="h-64 rounded-sm" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6 sm:gap-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant heading. */}
        <p className="text-sm font-sans text-tertiary leading-normal max-w-2xl">
          Outbound delivery URLs that Aaim signs and POSTs pledge events,
          collateral updates, and CHA alerts to. Each endpoint has its own
          HMAC secret you can rotate independently.
        </p>
        <Button variant="primary" onClick={openCreateModal}>
          <Plus className="w-4 h-4" />
          Add endpoint
        </Button>
      </div>

      {listQuery.error && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            Failed to load webhook endpoints
          </div>
        </div>
      )}

      {actionErrorMessage && (
        <Alert type="error" className="mb-6">
          {actionErrorMessage}
        </Alert>
      )}

      {!listQuery.isSuccess ? null : endpoints.length === 0 ? (
        // One action for one decision: the header's Add endpoint button is the
        // only primary control on the page (fi-g4-15). The description states
        // what the absence means, not only that the list is empty (fi-g4-18).
        <EmptyState
          title="No webhook endpoints are registered."
          description="Aaim delivers no pledge events outbound until one is added. Use Add endpoint to register the URL that should receive them."
        />
      ) : (
        // The kit table and status badge, the same pair Custodian Connections
        // uses for the same enabled/disabled state (fi-g4-12).
        <DataTable<TenantWebhookEndpoint>
          columns={endpointColumns}
          data={endpoints}
          rowKey="id"
          hoverable
        />
      )}

      {/* Create + Edit modal */}
      {modalMode !== null && (
        <EndpointFormModal
          key={editingEndpoint?.id ?? 'create'}
          mode={modalMode}
          baseline={editingEndpoint ?? undefined}
          onClose={() => {
            setModalMode(null);
            setEditingEndpoint(null);
            setFormErrorMessage(null);
          }}
          onSubmit={handleFormSubmit}
          isSubmitting={isFormSubmitting}
          errorMessage={formErrorMessage}
        />
      )}

      {/* Delete confirmation */}
      <DeleteConfirmationModal
        endpoint={deletingEndpoint}
        onClose={() => {
          setDeletingEndpoint(null);
          setDeleteErrorMessage(null);
        }}
        onConfirm={() => {
          if (deletingEndpoint) deleteMutation.mutate(deletingEndpoint);
        }}
        isDeleting={deleteMutation.isPending}
        errorMessage={deleteErrorMessage}
      />

      {/* Rotate confirmation */}
      <RotateConfirmationModal
        endpoint={rotatingEndpoint}
        onClose={() => setRotatingEndpoint(null)}
        onConfirm={() => {
          if (rotatingEndpoint) rotateMutation.mutate(rotatingEndpoint);
        }}
        isRotating={rotateMutation.isPending}
      />

      {/* One-time secret disclosure (create + rotate) */}
      <SecretDisclosureModal
        disclosure={secretDisclosure}
        onClose={() => setSecretDisclosure(null)}
      />
    </div>
  );
}
